import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ReactReduxContext } from 'react-redux';

import { ReactComponent as CopyIcon } from '../../assets/icons/copy.svg';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ToastMessage from '../toast-message/ToastMessage';

export interface CopyProperties {
  /** The value or data to copy to the clipboard. */
  value: string | number;
  /** UI style variant matching portal elements ('icon' | 'badge' | 'button'). Defaults to 'icon'. */
  variant?: 'icon' | 'badge' | 'button';
  /** Custom toast message text. Defaults to 'Copied to clipboard'. */
  toastMessage?: string;
  /** Whether to show the toast message notification. Defaults to true. */
  showToast?: boolean;
  /** Auto-dismiss duration in milliseconds for the toast. Defaults to 3000. */
  toastTimer?: number;
  /** Tooltip and accessible aria-label description. Defaults to 'Copy to clipboard'. */
  hoverLabel?: string;
  /** Tooltip placement relative to the copy element ('top' | 'bottom'). Defaults to 'top'. */
  tooltipPlacement?: 'top' | 'bottom';
  /** Delay in milliseconds before showing hover label to prevent flashing on rapid sweeps. Defaults to 120. */
  hoverDebounceMs?: number;
  /** Optional custom CSS class name applied to the root element. */
  className?: string;
  /** Size variant for the button ('small' | 'medium' | 'large'). Defaults to 'medium'. */
  size?: 'small' | 'medium' | 'large';
  /** Whether the copy button is disabled. Defaults to false. */
  disabled?: boolean;
  /** Optional custom icon to replace the default copy SVG. */
  customIcon?: React.ReactNode;
  /** Optional label or children displayed next to the icon. */
  children?: React.ReactNode;
  /** Optional callback triggered on copy success with the copied string. */
  onCopySuccess?: (copiedValue: string) => void;
  /** Optional callback triggered if clipboard copy fails with the error object. */
  onCopyError?: (error: Error) => void;
}

interface TooltipPosition {
  left: number;
  top: number;
  arrowOffset: number;
  placement: 'top' | 'bottom';
}

/**
 * Copies a string to the system clipboard with modern Clipboard API and fallback textarea.
 * @param {string} textToCopy - The text string to write to the clipboard.
 * @returns {Promise<boolean>} Resolves to true if write was successful, false otherwise.
 */
export async function copyTextToClipboard(textToCopy: string): Promise<boolean> {
  if (typeof window === 'undefined') {
    return false;
  }

  if (navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(textToCopy);
      return true;
    } catch {
      // Continue to execCommand fallback
    }
  }

  try {
    const textArea = document.createElement('textarea');
    textArea.value = textToCopy;
    textArea.className = 'copy-clipboard-textarea-hidden';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const isSuccessful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return isSuccessful;
  } catch {
    return false;
  }
}

/**
 * Resolves the SVG icon size based on the size prop.
 * @param {'small' | 'medium' | 'large'} [size] - Sizing variant.
 * @returns {number} Dimension in pixels.
 */
function resolveIconDimension(size?: 'small' | 'medium' | 'large'): number {
  if (size === 'small') return 14;
  if (size === 'large') return 20;
  return 16;
}

/**
 * Calculates clamped viewport coordinates for the hover label portal, preventing horizontal and vertical screen overflow.
 * @param {HTMLElement} triggerElement - The DOM element of the copy component wrapper.
 * @param {'top' | 'bottom'} preferredPlacement - Preferred vertical placement.
 * @returns {TooltipPosition} Clamped coordinates, arrow offset, and resolved placement.
 */
function calculateTooltipPosition(
  triggerElement: HTMLElement,
  preferredPlacement: 'top' | 'bottom',
): TooltipPosition {
  const triggerRectangle = triggerElement.getBoundingClientRect();
  const estimatedTooltipWidth = 130;
  const estimatedTooltipHeight = 28;
  const viewportPadding = 12;

  const triggerCenterX = triggerRectangle.left + triggerRectangle.width / 2;

  let left = triggerCenterX - estimatedTooltipWidth / 2;
  if (left + estimatedTooltipWidth > window.innerWidth - viewportPadding) {
    left = window.innerWidth - estimatedTooltipWidth - viewportPadding;
  }
  if (left < viewportPadding) {
    left = viewportPadding;
  }

  let arrowOffset = triggerCenterX - left;
  if (arrowOffset < 14) {
    arrowOffset = 14;
  } else if (arrowOffset > estimatedTooltipWidth - 14) {
    arrowOffset = estimatedTooltipWidth - 14;
  }

  let resolvedPlacement: 'top' | 'bottom' = preferredPlacement;
  let top: number;

  if (resolvedPlacement === 'top') {
    if (triggerRectangle.top - estimatedTooltipHeight - 8 < viewportPadding) {
      resolvedPlacement = 'bottom';
      top = triggerRectangle.bottom + 8;
    } else {
      top = triggerRectangle.top - estimatedTooltipHeight - 8;
    }
  } else if (
    triggerRectangle.bottom + estimatedTooltipHeight + 8 >
    window.innerHeight - viewportPadding
  ) {
    resolvedPlacement = 'top';
    top = triggerRectangle.top - estimatedTooltipHeight - 8;
  } else {
    top = triggerRectangle.bottom + 8;
  }

  return {
    left: Math.round(left),
    top: Math.round(top),
    arrowOffset: Math.round(arrowOffset),
    placement: resolvedPlacement,
  };
}

/**
 * Reusable Copy component displaying a copy icon that writes data to the clipboard,
 * debounces hover and screen events, clamps the hover label inside viewport edges,
 * portals the tooltip to prevent horizontal overflow-x, and triggers a toast notification.
 * @param {CopyProperties} properties - Component properties.
 * @returns {React.JSX.Element} The rendered Copy button and toast element.
 */
function Copy(properties: CopyProperties): React.JSX.Element {
  const {
    value,
    variant,
    toastMessage,
    showToast,
    toastTimer,
    hoverLabel,
    tooltipPlacement,
    hoverDebounceMs,
    className,
    size,
    disabled,
    customIcon,
    children,
    onCopySuccess,
    onCopyError,
  } = properties;

  const reduxContext = useContext(ReactReduxContext);
  const wrapperReference = useRef<HTMLDivElement>(null);
  const hoverEnterTimerReference = useRef<NodeJS.Timeout | null>(null);
  const hoverLeaveTimerReference = useRef<NodeJS.Timeout | null>(null);
  const isCopyingReference = useRef<boolean>(false);
  const feedbackTimeoutReference = useRef<NodeJS.Timeout | null>(null);

  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [isLocalToastOpen, setIsLocalToastOpen] = useState<boolean>(false);
  const [isTooltipActive, setIsTooltipActive] = useState<boolean>(false);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);

  /**
   * Updates tooltip coordinates based on current element position.
   */
  const updateTooltipPosition = (): void => {
    if (!wrapperReference.current || typeof window === 'undefined') {
      return;
    }

    const position = calculateTooltipPosition(
      wrapperReference.current,
      tooltipPlacement || 'top',
    );
    setTooltipPosition(position);
  };

  /**
   * Handles debounced mouse enter to prevent rapid hover label flashing.
   */
  const handleMouseEnter = (): void => {
    if (hoverLeaveTimerReference.current) {
      clearTimeout(hoverLeaveTimerReference.current);
      hoverLeaveTimerReference.current = null;
    }

    const debounceDelay = hoverDebounceMs ?? 120;
    hoverEnterTimerReference.current = setTimeout(() => {
      updateTooltipPosition();
      setIsTooltipActive(true);
    }, debounceDelay);
  };

  /**
   * Handles debounced mouse leave to smoothly dismiss the hover label.
   */
  const handleMouseLeave = (): void => {
    if (hoverEnterTimerReference.current) {
      clearTimeout(hoverEnterTimerReference.current);
      hoverEnterTimerReference.current = null;
    }

    hoverLeaveTimerReference.current = setTimeout(() => {
      setIsTooltipActive(false);
      setTooltipPosition(null);
    }, 100);
  };

  useEffect(() => {
    if (!isTooltipActive) {
      return undefined;
    }

    let screenDebounceTimer: NodeJS.Timeout | null = null;

    /**
     * Handles debounced screen resize and scroll boundary updates.
     */
    const handleScreenDebounce = (): void => {
      if (screenDebounceTimer) {
        clearTimeout(screenDebounceTimer);
      }
      screenDebounceTimer = setTimeout(() => {
        updateTooltipPosition();
      }, 100);
    };

    window.addEventListener('resize', handleScreenDebounce);
    window.addEventListener('scroll', handleScreenDebounce, true);

    return () => {
      if (screenDebounceTimer) {
        clearTimeout(screenDebounceTimer);
      }
      window.removeEventListener('resize', handleScreenDebounce);
      window.removeEventListener('scroll', handleScreenDebounce, true);
    };
  }, [isTooltipActive]);

  useEffect(() => {
    return () => {
      if (feedbackTimeoutReference.current) {
        clearTimeout(feedbackTimeoutReference.current);
      }
      if (hoverEnterTimerReference.current) {
        clearTimeout(hoverEnterTimerReference.current);
      }
      if (hoverLeaveTimerReference.current) {
        clearTimeout(hoverLeaveTimerReference.current);
      }
    };
  }, []);

  /**
   * Handles the click event with debounce protection, copies data, and triggers notifications.
   * @param {React.MouseEvent<HTMLButtonElement>} event - The mouse click event.
   * @returns {Promise<void>} Resolves when copy workflow completes.
   */
  const handleCopy = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.stopPropagation();

    if (disabled || value === null || value === undefined || isCopyingReference.current) {
      return;
    }

    isCopyingReference.current = true;
    const textToCopy = String(value);

    try {
      const isCopySuccessful = await copyTextToClipboard(textToCopy);

      if (!isCopySuccessful) {
        throw new Error('Clipboard write operation failed');
      }

      setIsCopied(true);
      if (feedbackTimeoutReference.current) {
        clearTimeout(feedbackTimeoutReference.current);
      }
      feedbackTimeoutReference.current = setTimeout(() => {
        setIsCopied(false);
      }, 1500);

      if (showToast) {
        const resolvedMessage = toastMessage || 'Copied to clipboard';
        if (reduxContext?.store?.dispatch) {
          reduxContext.store.dispatch(
            setNotificationMessage({
              status: true,
              message: resolvedMessage,
              type: 'success',
              code: 200,
            }),
          );
        } else {
          setIsLocalToastOpen(true);
        }
      }

      if (onCopySuccess) {
        onCopySuccess(textToCopy);
      }
    } catch (error) {
      const copyError =
        error instanceof Error ? error : new Error('Failed to copy to clipboard');
      if (onCopyError) {
        onCopyError(copyError);
      }
    } finally {
      setTimeout(() => {
        isCopyingReference.current = false;
      }, 400);
    }
  };

  /**
   * Closes the standalone local toast message.
   */
  const handleCloseToast = (): void => {
    setIsLocalToastOpen(false);
  };

  const labelContent = children ?? (variant === 'badge' ? value : null);
  const iconDimension = resolveIconDimension(size);

  const wrapperClasses = [
    'copy-component-wrap',
    `variant-${variant || 'icon'}`,
    size || 'medium',
    className || '',
    disabled ? 'disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={wrapperReference}
      className={wrapperClasses}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className={`copy-button ${isCopied ? 'copied' : ''}`}
        onClick={handleCopy}
        disabled={disabled}
        aria-label={hoverLabel || 'Copy to clipboard'}
        title={hoverLabel || 'Copy to clipboard'}
      >
        {labelContent && <span className="copy-label">{labelContent}</span>}
        <span className="copy-icon-wrap" aria-hidden="true">
          {customIcon ||
            (isCopied ? (
              <svg
                className="copy-check-icon"
                width={iconDimension}
                height={iconDimension}
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.3332 4L5.99984 11.3333L2.6665 8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <CopyIcon />
            ))}
        </span>
      </button>

      {isTooltipActive &&
        hoverLabel &&
        !disabled &&
        tooltipPosition &&
        ReactDOM.createPortal(
          <span
            className={`copy-hover-tooltip placement-${tooltipPosition.placement}`}
            style={
              {
                left: `${tooltipPosition.left}px`,
                top: `${tooltipPosition.top}px`,
                '--arrow-left': `${tooltipPosition.arrowOffset}px`,
              } as React.CSSProperties
            }
            role="tooltip"
          >
            {hoverLabel}
          </span>,
          document.body,
        )}

      {showToast && !reduxContext?.store?.dispatch && (
        <ToastMessage
          open={isLocalToastOpen}
          type="success"
          message={toastMessage || 'Copied to clipboard'}
          onClose={handleCloseToast}
          onCloseTimer={toastTimer}
        />
      )}
    </div>
  );
}

Copy.defaultProps = {
  variant: 'icon',
  toastMessage: 'Copied to clipboard',
  showToast: true,
  toastTimer: 3000,
  hoverLabel: 'Copy to clipboard',
  tooltipPlacement: 'top',
  hoverDebounceMs: 120,
  className: '',
  size: 'medium',
  disabled: false,
  customIcon: null,
  children: null,
  onCopySuccess: null,
  onCopyError: null,
};

export default Copy;
